import type {
  DeckCardTocExp,
  DeckCardTocItem,
} from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import {
  formatDeckCardTypeLabel,
  mergeDeckCardToc,
} from "@/cards/common/types/app-deck/deckCardThumbnail.types";

export { mergeDeckCardToc };

export type DeckCardTocTranslate = (
  key: string,
  options?: { defaultValue?: string; count?: number }
) => string;

const translate = (
  t: DeckCardTocTranslate | undefined,
  key: string,
  fallback: string,
  options?: { count?: number }
): string => {
  if (!t) {
    if (options?.count != null) {
      return fallback.replace("{{count}}", String(options.count));
    }
    return fallback;
  }
  return t(key, { defaultValue: fallback, ...options });
};
export const stripDeckCardHtml = (value?: string): string =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

export const normalizeDeckCardTypeKey = (type?: string): string =>
  (type || "")
    .replace(/[\s-]+/g, "_")
    .replace(/__+/g, "_")
    .toLowerCase();

export const isDemoCardType = (type?: string): boolean => {
  const key = normalizeDeckCardTypeKey(type);
  return key === "demo_card" || key === "democard";
};

export const isSeasonCardType = (type?: string): boolean => {
  const key = normalizeDeckCardTypeKey(type);
  return (
    key === "season_card" ||
    key === "seasoncard" ||
    key.startsWith("season_card") ||
    key.includes("seasoncard")
  );
};

export const formatTocTypeLabel = (
  type?: string,
  t?: DeckCardTocTranslate
): string | undefined => {
  if (!type) return undefined;
  if (isDemoCardType(type) || isSeasonCardType(type)) {
    return translate(t, "deck_toc_episode", "Episode");
  }
  return formatDeckCardTypeLabel(type);
};

export const isDemoOrListeningTogetherType = (type?: string): boolean => {
  const key = normalizeDeckCardTypeKey(type);
  return (
    isDemoCardType(type) ||
    key === "listening_together_card" ||
    key === "listen_together_card" ||
    key === "listeningtogethercard" ||
    key === "listentogethercard"
  );
};

const MS_PER_DAY = 86_400_000;

export const formatTocReleaseLabel = (
  iso?: string | null,
  t?: DeckCardTocTranslate
): string | null => {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfTarget = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate()
  );
  const diffDays = Math.round(
    (startOfTarget.getTime() - startOfToday.getTime()) / MS_PER_DAY
  );

  if (diffDays < 0) {
    return translate(t, "deck_toc_released", "Released");
  }
  if (diffDays === 0) {
    return translate(t, "deck_toc_releases_today", "Releases today");
  }
  if (diffDays === 1) {
    return translate(t, "deck_toc_releases_in_one_day", "Releases in 1 day");
  }
  return translate(
    t,
    "deck_toc_releases_in_days",
    "Releases in {{count}} days",
    { count: diffDays }
  );
};

export const getTocExpReleaseLabel = (
  exp: DeckCardTocExp,
  t?: DeckCardTocTranslate
): string | null =>
  formatTocReleaseLabel(exp.release_date || exp.start_time || null, t);

export const getTocItemDescription = (
  item: DeckCardTocItem
): string | undefined => {
  const text = stripDeckCardHtml(item.description);
  return text || undefined;
};

export const getTocItemTypeLabel = (
  item: DeckCardTocItem,
  t?: DeckCardTocTranslate
): string | undefined => {
  if (!item.type) return undefined;
  return formatTocTypeLabel(item.type, t);
};

export type DeckCardTocDisplayEntry = {
  key: string;
  item: DeckCardTocItem;
  exp?: DeckCardTocExp;
  title: string;
  typeLabel?: string;
  description?: string;
  releaseLabel?: string | null;
  sessionMeta?: string;
};

export const buildTocDisplayEntries = (
  items: DeckCardTocItem[],
  t?: DeckCardTocTranslate
): DeckCardTocDisplayEntry[] => {
  const entries: DeckCardTocDisplayEntry[] = [];

  items.forEach((item, itemIndex) => {
    // Nested exps (demo, season, listening together) → one row per experience.
    if (item.exps?.length) {
      item.exps.forEach((exp, expIndex) => {
        entries.push({
          key: `exp-${itemIndex}-${exp.id ?? expIndex}`,
          item,
          exp,
          title:
            exp.title ||
            exp.theme_title ||
            translate(t, "deck_toc_experience", "Experience"),
          typeLabel: exp.type
            ? formatTocTypeLabel(exp.type, t)
            : getTocItemTypeLabel(item, t),
          releaseLabel: getTocExpReleaseLabel(exp, t),
          sessionMeta:
            [exp.session_type, exp.status].filter(Boolean).join(" · ") ||
            undefined,
        });
      });
      return;
    }

    entries.push({
      key: `item-${itemIndex}-${item.type ?? "page"}-${item.title ?? "untitled"}`,
      item,
      title:
        item.title ||
        formatTocTypeLabel(item.type, t) ||
        translate(t, "deck_toc_untitled", "Untitled"),
      typeLabel: getTocItemTypeLabel(item, t),
      description: getTocItemDescription(item),
      releaseLabel: null,
    });
  });

  return entries;
};
